import React, { createContext, ReactNode } from 'react';
import { usePermissions } from './Permissions.provider';

export type SupportedModules =
  | 'dashboard'
  | 'accounts'
  | 'transactions'
  | 'billingPackages'
  | 'reports'
  | 'statements'
  | 'leads'
  | 'support'
  | 'settings'
  | 'users'
  | 'onBoardingFormTracker';

export type FeatureFlagCtx = {
  modules?: {
    dashboard?: {
      isEnabled: boolean;
      displayAccountProfile: boolean;
    };
    leads?: {
      isEnabled: boolean;
    };
    accounts?: {
      isEnabled: boolean;
      canCreateAccount: boolean;
      canUpdatePermissions: boolean;
      canAccessBillingTab: boolean;
      canAccessOnboardingURL: boolean;
      canAccessOnboardingTokenPanel: boolean;
      canAccessCustomServiceRequirements: boolean;
      canAccessCustomTransactionEstimates: boolean;
      canSkipCustomTransactionEstimates: boolean;
      canRefundTransactions: boolean;
      canSeeAccountCredential: boolean;
      canAccessActivityReport: boolean;
      canAccessBillingDetailsReport: boolean;
      canSeeBusinessCase: boolean;
      canSendAgreement: boolean;
      canUpdateSigningAuthorities: boolean;
      partnerBillsAccount: boolean;
    };
    billingPackages?: {
      isEnabled: boolean;
      canCreatePackage: boolean;
      canUsePayAsYouGo: boolean;
    };
    reports?: {
      isEnabled: boolean;
      canAccessActivityReport: boolean;
      canAccessBillingDetailsReport: boolean;
    };
    statements?: {
      isEnabled: boolean;
    };
    settings?: {
      isEnabled: boolean;
    };
    support?: {
      isEnabled: boolean;
    };
    onBoardingFormTracker?: {
      isEnabled: boolean;
    };
    users?: {
      isEnabled: boolean;
    };
    transactions?: {
      isEnabled: boolean;
    };
  };
};

export const FeatureFlagContext = createContext<FeatureFlagCtx>({});

export const FeatureFlagProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const { permissions } = usePermissions();

  const values: FeatureFlagCtx = {
    modules: {
      dashboard: {
        isEnabled: true,
        displayAccountProfile: true,
      },
      leads: {
        isEnabled: permissions.leadManagementEnabled,
      },
      accounts: {
        isEnabled: permissions.accountManagementEnabled,
        canCreateAccount: permissions.CanAddCanadianAccounts || permissions.CanAddUSAccounts,
        canUpdatePermissions: permissions.setAccountPermissionsEnabled,
        canAccessBillingTab: permissions.accountBillingPackageRequired,
        canAccessOnboardingURL: permissions.accountOnboardingFormAccess,
        canAccessOnboardingTokenPanel: permissions.accountOnboardingFormAccess,
        canAccessCustomServiceRequirements: !permissions.accountBillingPackageRequired,
        canAccessCustomTransactionEstimates: true,
        canSkipCustomTransactionEstimates: !permissions.transactionEstimatesRequired,
        canRefundTransactions: permissions.accountEndpointAccess,
        canAccessActivityReport: permissions.leadManagementEnabled,
        canSeeAccountCredential: permissions.accountCredentialsAccess,
        canAccessBillingDetailsReport: !permissions.leadManagementEnabled,
        canSeeBusinessCase: permissions.businessCaseRequired,
        canSendAgreement: permissions.businessCaseRequired,
        canUpdateSigningAuthorities: !permissions.partnerLiability,
        partnerBillsAccount: permissions.PartnerBillsAccount,
      },
      billingPackages: {
        isEnabled: permissions.accountManagementEnabled,
        canCreatePackage: permissions.customBillingPackageCreationEnabled,
        canUsePayAsYouGo: false,
      },
      reports: {
        isEnabled: true,
        canAccessActivityReport: true,
        canAccessBillingDetailsReport: permissions.accountManagementEnabled && !permissions.PartnerBillsAccount,
      },
      statements: {
        isEnabled: true,
      },
      onBoardingFormTracker: {
        isEnabled: permissions.accountOnboardingFormAccess,
      },
      settings: {
        isEnabled: true,
      },
      support: {
        isEnabled: true,
      },
      transactions: {
        isEnabled: permissions.accountManagementEnabled,
      },

    },
  };
  return <FeatureFlagContext.Provider value={values}>{children}</FeatureFlagContext.Provider>;
};

const useFeatureFlags = (): FeatureFlagCtx => React.useContext(FeatureFlagContext);

export const useModuleFeatureFlag = (module: SupportedModules): { [feature: string]: boolean } => {
  const featureFlags = useFeatureFlags();
  return featureFlags.modules?.[module] || {};
};
